// src/lib/claudeApi.js
import { auth } from "../firebase";

const CLAUDE_PROXY_URL =
  process.env.REACT_APP_CLAUDE_PROXY_URL || "https://claude-vc2yjokjaq-uc.a.run.app";

export const MODELS = {
  TASK_SUGGESTION: "claude-opus-4-7",
  INLINE_UX: "claude-sonnet-4-6",
};

export async function callClaude(system, userContent, options = {}) {
  const { model = MODELS.TASK_SUGGESTION, maxTokens = 600 } = options;
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("Not authenticated");
  const token = await currentUser.getIdToken();
  const res = await fetch(CLAUDE_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
    body: JSON.stringify({
      model,
      max_tokens: maxTokens,
      system,
      messages: [{ role: "user", content: userContent }],
    }),
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  const data = await res.json();
  return data.content?.map((b) => b.text || "").join("") || "";
}

function buildVendorContext(vendors) {
  if (!vendors?.length) return "";
  return `\n\nVendors available to this operation:\n${vendors.map((v) =>
    `- ${v.name}: ${v.scope}${v.phone ? ` | Phone: ${v.phone}` : ""}${v.email ? ` | Email: ${v.email}` : ""}`
  ).join("\n")}\n\nIf any vendor's scope covers the issue(s), the task MUST name that vendor, state what to ask them to do, and include their phone and/or email.`;
}

const MAX_INPUT_POSTS = 25;
const MAX_POST_IDS = 8;

function topPosts(posts, limit = MAX_INPUT_POSTS) {
  return [...posts]
    .sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0))
    .slice(0, limit);
}

function summarizePosts(posts) {
  return posts
    .map((p) => `ID: ${p.id}\nCategory: ${p.category} | Severity: ${p.severity}\n${p.description}`)
    .join("\n\n");
}

export async function suggestTask(posts, vendors = []) {
  const sample = topPosts(posts, MAX_INPUT_POSTS);
  const system = `You are helping operations management create actionable tasks from reported issues.
Review the provided pinch points and suggest ONE focused, achievable task that addresses the most pressing issue or a group of closely related issues.${buildVendorContext(vendors)}
Respond ONLY with valid JSON in this exact shape (no other text):
{"title":"short action-oriented title (max 60 chars)","description":"2-3 sentence description of what needs to be done and why","postIds":["id1","id2"]}
The postIds array must only contain IDs from the provided posts that are directly relevant to this task. Include at most ${MAX_POST_IDS} postIds.`;
  const text = await callClaude(system, `Unassigned posts:\n\n${summarizePosts(sample)}`, { maxTokens: 800 });
  return JSON.parse(text.replace(/```json|```/g, "").trim());
}

export async function suggestTaskFromPost(post, vendors = []) {
  const system = `You are helping operations management create actionable tasks from reported issues.
Based on this single reported pinch point, generate ONE focused, actionable task.${buildVendorContext(vendors)}
Respond ONLY with valid JSON in this exact shape (no other text):
{"title":"short action-oriented title (max 60 chars)","description":"2-3 sentence description of what needs to be done and why"}`;
  const text = await callClaude(
    system,
    `Generate a single actionable task based on this reported issue: ${post.category} — ${post.description}`,
    { maxTokens: 600 }
  );
  return JSON.parse(text.replace(/```json|```/g, "").trim());
}

export async function bulkSuggestTasks(posts, vendors = []) {
  const sample = topPosts(posts, MAX_INPUT_POSTS);
  const system = `You are helping operations management create actionable tasks from reported issues.
Review the provided pinch points and suggest exactly 5 focused, achievable tasks that together address distinct clusters of issues. If there are fewer than 5 clear clusters, combine related issues into broader tasks to still return exactly 5.${buildVendorContext(vendors)}
Respond ONLY with a valid JSON array of exactly 5 objects (no other text):
[{"title":"short action-oriented title (max 60 chars)","description":"2-3 sentence description of what needs to be done and why","postIds":["id1","id2"]},...]
Each task's postIds must only contain IDs from the provided posts relevant to that task. Include at most ${MAX_POST_IDS} postIds per task. Minimize post overlap between tasks.`;
  const text = await callClaude(system, `Unassigned posts:\n\n${summarizePosts(sample)}`, { maxTokens: 2500 });
  const parsed = JSON.parse(text.replace(/```json|```/g, "").trim());
  if (!Array.isArray(parsed)) throw new Error("Expected array");
  return parsed;
}
